import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  IconButton,
  Grid,
  Badge,
  Image,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import geoContenedor1 from '../assets/images/Geo-contenedor-1.webp';
import geoContenedor2 from '../assets/images/Geo-contenedor-2.webp';
import geoContenedor3 from '../assets/images/Geo-contenedor-3.webp';

import retroexcavadora from '../assets/images/retroexcavador_1.webp';
import retroexcavadora2 from '../assets/images/retroexcavador_2.webp';
import retroexcavadora3 from '../assets/images/retroexcavador_3.webp';

import miniretro from '../assets/images/Miniretrocargadora_1.webp';
import miniretro2 from '../assets/images/Miniretrocargadora_2.webp';
import miniretro3 from '../assets/images/Miniretrocargadora_3.webp';

import pavimentadora1 from '../assets/images/Pavimentadora_1.webp';
import pavimentadora2 from '../assets/images/Pavimentadora_2.webp';
import pavimentadora3 from '../assets/images/Pavimentadora_3.webp';

import trituradora1 from '../assets/images/trituradora_1.webp';
import trituradora2 from '../assets/images/trituradora_2.webp';
import trituradora3 from '../assets/images/trituradora_3.webp';

const projects = [
  {
    id: 1,
    icon: '🚜',
    category: 'MAQUINARIA',
    route: 'SHANGHÁI A SANTA MARTA',
    title: 'Importación de Retroexcavadora Industrial',
    description: 'Gestión completa de la importación de una retroexcavadora desde China, incluyendo coordinación con proveedor, transporte marítimo y trámites de nacionalización en Colombia.',
    images: [
      retroexcavadora,
      retroexcavadora2,
      retroexcavadora3,
    ],
    categoryColor: 'blue.400',
  },
  {
    id: 2,
    icon: '🚜',
    category: 'MAQUINARIA',
    route: 'SHANGHÁI A SANTA MARTA',
    title: 'Minicargador para Construcción',
    description: 'Gestión integral de la importación de una retroexcavadora compacta desde China, abarcando la selección del proveedor, logística internacional, transporte marítimo y proceso de nacionalización en Colombia.',
    images: [
      miniretro,
      miniretro2,
      miniretro3,
    ],
    categoryColor: 'blue.400',
  },
  {
    id: 3,
    icon: '🏗️',
    category: 'MAQUINARIA',
    route: 'SHANGHÁI A SANTA MARTA',
    title: 'Equipo de Pulverización de Piedra',
    description: 'Importación de maquinaria industrial para procesamiento de material rocoso, con logística especializada para carga de gran volumen y peso.',
    images: [
      trituradora1,
      trituradora2,
      trituradora3,
    ],
    categoryColor: 'blue.400',
  },
  {
    id: 4,
    icon: '🛣️',
    category: 'MAQUINARIA',
    route: 'SHENZHEN A SANTA MARTA',
    title: 'Máquina Pavimentadora de Carreteras',
    description: 'Equipos especializados para la elaboración y pavimentación de carreteras, destinados a proyectos de infraestructura vial.',
    images: [
      pavimentadora1,
      pavimentadora2,
      pavimentadora3,
    ],
    categoryColor: 'blue.400',
  },

  {
    id: 5,
    icon: '🧵',
    category: 'CONSTRUCCIÓN',
    route: 'SHENZHEN A SANTA MARTA',
    title: 'Geocontenedores para Infraestructura',
    description: 'Transporte e importación de geocontenedores para proyectos de construcción, asegurando entrega eficiente y seguimiento continuo durante el trayecto.',
    images: [
      geoContenedor1,
      geoContenedor2,
      geoContenedor3,
    ],
    categoryColor: 'blue.400',
  },
];

// Componente de tarjeta individual con galería automática
function ProjectCard({ project }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const cardBg = useColorModeValue('rgba(255, 255, 255, 0.8)', 'rgba(15, 23, 42, 0.6)');
  const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const imageBg = useColorModeValue('gray.100', 'rgba(30, 41, 59, 0.8)');
  const descriptionColor = useColorModeValue('gray.600', 'gray.400');
  const titleColor = useColorModeValue('gray.800', 'white');

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === project.images.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000); // Cambia cada 3 segundos

    return () => clearInterval(interval);
  }, [project.images.length]);

  const handleDotClick = (index) => {
    setCurrentImageIndex(index);
  };

  return (
    <Box
      bg={cardBg}
      borderRadius="2xl"
      overflow="hidden"
      border="1px solid"
      borderColor={cardBorder}
    >
      {/* Image Gallery */}
      <Box
        h="240px"
        bg={imageBg}
        position="relative"
        overflow="hidden"
      >
        {/* Images */}
        {project.images.map((img, index) => (
          <Box
            key={index}
            position="absolute"
            top={0}
            left={0}
            w="full"
            h="full"
            opacity={currentImageIndex === index ? 1 : 0}
            transition="opacity 0.5s ease-in-out"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Image
              src={img}
              alt={`${project.title} Image ${index + 1}`}
              objectFit="cover"
              w="full"
              h="full"
              loading="lazy"
            />

          </Box>
        ))}

        {/* Dots Indicator */}
        <HStack
          position="absolute"
          bottom={4}
          left="50%"
          transform="translateX(-50%)"
          spacing={2}
          zIndex={2}
        >
          {project.images.map((_, index) => (
            <Box
              key={index}
              w="8px"
              h="8px"
              borderRadius="full"
              bg={currentImageIndex === index ? 'brand.500' : 'whiteAlpha.400'}
              cursor="pointer"
              transition="all 0.3s"
              onClick={() => handleDotClick(index)}
              _hover={{ bg: currentImageIndex === index ? 'brand.500' : 'whiteAlpha.600' }}
            />
          ))}
        </HStack>
      </Box>

      {/* Content */}
      <VStack align="start" spacing={3} p={6}>
        <HStack spacing={3} flexWrap="wrap">
          <Badge
            bg={project.categoryColor}
            color="gray.900"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="xs"
            fontWeight="bold"
            textTransform="uppercase"
          >
            {project.category}
          </Badge>
          <Text fontSize="xs" color="gray.500" fontWeight="medium">
            • {project.route}
          </Text>
        </HStack>

        <Heading
          as="h3"
          fontSize="xl" color={titleColor}
          lineHeight="short"
          fontWeight="700"
        >
          {project.icon} {project.title}
        </Heading>

        <Text fontSize="sm" color={descriptionColor} lineHeight="tall">
          {project.description}
        </Text>
      </VStack>
    </Box>
  );
}

function ProjectsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = 3;
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const iconButtonBg = useColorModeValue('white', 'whiteAlpha.200');

  const handlePrev = () => {
    setCurrentIndex((prev) => {
      if (prev === 0) {
        return projects.length - 1;
      }
      return prev - 1;
    });
  };

  const handleNext = () => {
    setCurrentIndex((prev) => {
      if (prev >= projects.length - 1) {
        return 0;
      }
      return prev + 1;
    });
  };

  // Get visible projects with wrapping (circular carousel)
  const getVisibleProjects = () => {
    const visible = [];
    for (let i = 0; i < itemsPerPage; i++) {
      const index = (currentIndex + i) % projects.length;
      visible.push(projects[index]);
    }
    return visible;
  };

  const visibleProjects = getVisibleProjects();

  return (
    <Box id="proyectos" as="section" w="full" py={{ base: 16, md: 20 }}>
      <Container maxW="1120px">
        <VStack spacing={10} align="stretch">
          {/* Header */}
          <HStack justify="space-between" align="start" flexWrap="wrap" gap={2}>
            <VStack align="start" spacing={3} maxW="1000px">
              <Heading 
                as="h2" 
                fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                fontWeight={900}
              >
                Proyectos Exitosos
              </Heading>
              <Text
                fontSize={{ base: 'lg', md: 'xl' }}
                color={subtitleColor}
                letterSpacing="wide"
                lineHeight="tall"
                pb={3}
                mb={2}
              >
                Explora nuestro portafolio de envíos entregados exitosamente en diversas industrias.
              </Text>
            </VStack>

            {/* Navigation Controls - Only visible on desktop */}
            <HStack spacing={3} display={{ base: 'none', md: 'flex' }}>
              <IconButton
                icon={<ChevronLeftIcon boxSize={6} />}
                onClick={handlePrev}
                aria-label="Proyecto anterior"
                bg={iconButtonBg}
                _hover={{ bg: 'brand.500', color: 'white' }}
                rounded="full"
                size="lg"
              />
              <IconButton
                icon={<ChevronRightIcon boxSize={6} />}
                onClick={handleNext}
                aria-label="Siguiente proyecto"
                bg={iconButtonBg}
                _hover={{ bg: 'brand.500', color: 'white' }}
                rounded="full"
                size="lg"
              />
            </HStack>
          </HStack>

          {/* Projects Grid - Mobile: All projects, Desktop: Carousel */}
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
            gap={6}
          >
            {/* Mobile: Show all projects */}
            <Box display={{ base: 'contents', md: 'none' }}>
              {projects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </Box>

            {/* Desktop: Show carousel (3 projects at a time) */}
            <Box display={{ base: 'none', md: 'contents' }}>
              {visibleProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </Box>
          </Grid>
        </VStack>
      </Container>
    </Box>
  );
}

export default ProjectsSection;
